import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import {
  Person,
  Email,
  Lock,
  Visibility,
  VisibilityOff,
  School,
  Phone,
  PersonAdd,
  Psychology as Brain,
  Grade,
  Business,
  Info,
  Numbers,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { RegisterRequest } from '../services/authService';

// Animations
const slideInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
`;

// Styled Components
const RegisterContainer = styled(Box)`
  min-height: calc(100vh - 80px);
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%) !important;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  padding: 2rem 0;
`;

const RegisterCard = styled(Card)`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  animation: ${slideInUp} 0.6s ease-out;
  position: relative;
  z-index: 2;
  max-width: 1200px;
  width: 100%;
  
  @media (max-width: 1024px) {
    max-width: 900px;
  }
  
  @media (max-width: 768px) {
    max-width: 500px;
  }
`;

const BrandSection = styled(Box)`
  text-align: center;
  margin-bottom: 1.5rem;
  
  .logo {
    animation: ${float} 3s ease-in-out infinite;
  }
`;

const StyledTextField = styled(TextField)`
  margin-bottom: 1rem;
  
  .MuiOutlinedInput-root {
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.8);
    
    &:hover {
      background: rgba(255, 255, 255, 0.9);
    }
    
    &.Mui-focused {
      background: rgba(255, 255, 255, 1);
    }
  }
`;

const StyledFormControl = styled(FormControl)`
  margin-bottom: 1rem;
  
  .MuiOutlinedInput-root {
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.8);
    
    &:hover {
      background: rgba(255, 255, 255, 0.9);
    }
    
    &.Mui-focused {
      background: rgba(255, 255, 255, 1);
    }
  }
`;

const RegisterButton = styled(Button)`
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  padding: 1rem 2rem;
  border-radius: 12px;
  font-weight: 700;
  font-size: 1rem;
  text-transform: none;
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.3);
  transition: all 0.3s ease;
  margin-bottom: 1.5rem;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 35px rgba(102, 126, 234, 0.4);
  }
  
  &:disabled {
    background: linear-gradient(135deg, #94a3b8, #cbd5e1);
    transform: none;
    box-shadow: none;
  }
`;

const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const { register, isLoading } = useAuth();
  
  const [formData, setFormData] = useState<RegisterRequest>({
    name: '',
    email: '',
    password: '',
    grade: 1,
    school: '',
    schoolRegistrationNumber: '',
    phone: '',
    parentName: '',
    parentPhone: '',
  });
  
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({});

  const validateForm = () => {
    const errors: { [key: string]: string } = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Full name is required';
    } else if (formData.name.trim().length < 2) {
      errors.name = 'Name must be at least 2 characters';
    }
    
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Please enter a valid email';
    }
    
    if (!formData.password) {
      errors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }
    
    if (!confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    
    if (!formData.school.trim()) {
      errors.school = 'School name is required';
    }
    
    if (!formData.schoolRegistrationNumber.trim()) {
      errors.schoolRegistrationNumber = 'School registration number is required';
    }
    
    if (!formData.phone.trim()) {
      errors.phone = 'Phone number is required';
    } else if (!/^[0-9]{10}$/.test(formData.phone)) {
      errors.phone = 'Please enter a valid 10-digit phone number';
    }
    
    if (!formData.parentName.trim()) {
      errors.parentName = 'Parent/Guardian name is required';
    }
    
    if (!formData.parentPhone.trim()) {
      errors.parentPhone = 'Parent/Guardian phone is required';
    } else if (!/^[0-9]{10}$/.test(formData.parentPhone)) {
      errors.parentPhone = 'Please enter a valid 10-digit phone number';
    }
    
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setError(null);
    
    try {
      await register(formData);
      navigate('/dashboard', { replace: true });
    } catch (err: any) {
      setError(err.message || 'Registration failed. Please try again.');
    }
  };

  const handleInputChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = field === 'grade' ? Number(e.target.value) : e.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear field error when user starts typing
    if (fieldErrors[field]) {
      setFieldErrors(prev => ({ ...prev, [field]: '' }));
    }
    
    // Clear general error
    if (error) {
      setError(null);
    }
  };

  const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConfirmPassword(e.target.value);
    
    // Clear field error when user starts typing
    if (fieldErrors.confirmPassword) {
      setFieldErrors(prev => ({ ...prev, confirmPassword: '' }));
    }
    
    // Clear general error
    if (error) {
      setError(null);
    }
  };

  return (
    <RegisterContainer sx={{ px: { xs: 2, sm: 4, md: 6, lg: 8 } }}>
      <RegisterCard>
        <CardContent sx={{ p: 3 }}>
            <BrandSection>
              <Box className="logo" mb={2}>
                <Brain sx={{ fontSize: 56, color: '#667eea' }} />
              </Box>
              <Typography variant="h5" fontWeight={700} gutterBottom>
                Join Apex Scholar League
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Start your AI-powered learning journey today
              </Typography>
            </BrandSection>

            {error && (
              <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit}>
              {/* Personal & Account Information */}
              <Typography variant="h6" fontWeight={600} mb={1.5} color="primary">
                Personal & Account Information
              </Typography>
              
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, 
                gap: 2,
                mb: 2
              }}>
                <StyledTextField
                  fullWidth
                  label="Full Name"
                  value={formData.name}
                  onChange={handleInputChange('name')}
                  error={!!fieldErrors.name}
                  helperText={fieldErrors.name}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Person sx={{ color: 'text.secondary' }} />
                      </InputAdornment>
                    ),
                  }}
                  disabled={isLoading}
                  required
                />

                <StyledTextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange('email')}
                  error={!!fieldErrors.email}
                  helperText={fieldErrors.email}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email sx={{ color: 'text.secondary' }} />
                      </InputAdornment>
                    ),
                  }}
                  disabled={isLoading}
                  required
                />

                <StyledFormControl fullWidth>
                  <InputLabel>Grade *</InputLabel>
                  <Select
                    value={formData.grade}
                    label="Grade *"
                    onChange={(e) => setFormData(prev => ({ ...prev, grade: Number(e.target.value) }))}
                    startAdornment={<Grade sx={{ mr: 1, color: 'text.secondary' }} />}
                    disabled={isLoading}
                    required
                  >
                    {Array.from({ length: 12 }, (_, i) => (
                      <MenuItem key={i + 1} value={i + 1}>
                        Grade {i + 1}
                      </MenuItem>
                    ))}
                  </Select>
                </StyledFormControl>
              </Box>

              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, 
                gap: 2,
                mb: 2
              }}>
                <StyledTextField
                  fullWidth
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleInputChange('password')}
                  error={!!fieldErrors.password}
                  helperText={fieldErrors.password}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock sx={{ color: 'text.secondary' }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          disabled={isLoading}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  disabled={isLoading}
                  required
                />

                <StyledTextField
                  fullWidth
                  label="Confirm Password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={handleConfirmPasswordChange}
                  error={!!fieldErrors.confirmPassword}
                  helperText={fieldErrors.confirmPassword}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock sx={{ color: 'text.secondary' }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          edge="end"
                          disabled={isLoading}
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  disabled={isLoading}
                  required
                />
              </Box>

              {/* Academic Information */}
              <Typography variant="h6" fontWeight={600} mb={1.5} color="primary" mt={2}>
                Academic Information
              </Typography>

              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, 
                gap: 2,
                mb: 2
              }}>
                <StyledTextField
                  fullWidth
                  label="School Name"
                  value={formData.school}
                  onChange={handleInputChange('school')}
                  error={!!fieldErrors.school}
                  helperText={fieldErrors.school}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Business sx={{ color: 'text.secondary' }} />
                      </InputAdornment>
                    ),
                  }}
                  disabled={isLoading}
                  required
                />

                <StyledTextField
                  fullWidth
                  label="School Registration Number"
                  value={formData.schoolRegistrationNumber}
                  onChange={handleInputChange('schoolRegistrationNumber')}
                  error={!!fieldErrors.schoolRegistrationNumber}
                  helperText={fieldErrors.schoolRegistrationNumber}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Numbers sx={{ color: 'text.secondary' }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip 
                          title="Registration number with education boards like GSEB, CBSE, NCERT, etc."
                          placement="top"
                        >
                          <Info sx={{ color: 'text.secondary', cursor: 'help' }} />
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                  disabled={isLoading}
                  placeholder="e.g., GSEB123456, CBSE789012"
                  required
                />
              </Box>

              {/* Contact Information */}
              <Typography variant="h6" fontWeight={600} mb={1.5} color="primary" mt={2}>
                Contact Information
              </Typography>

              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, 
                gap: 2,
                mb: 2
              }}>
                <StyledTextField
                  fullWidth
                  label="Your Phone Number"
                  value={formData.phone}
                  onChange={handleInputChange('phone')}
                  error={!!fieldErrors.phone}
                  helperText={fieldErrors.phone}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Phone sx={{ color: 'text.secondary' }} />
                      </InputAdornment>
                    ),
                  }}
                  disabled={isLoading}
                  placeholder="10-digit number"
                  required
                />

                <StyledTextField
                  fullWidth
                  label="Parent/Guardian Name"
                  value={formData.parentName}
                  onChange={handleInputChange('parentName')}
                  error={!!fieldErrors.parentName}
                  helperText={fieldErrors.parentName}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Person sx={{ color: 'text.secondary' }} />
                      </InputAdornment>
                    ),
                  }}
                  disabled={isLoading}
                  required
                />

                <StyledTextField
                  fullWidth
                  label="Parent/Guardian Phone"
                  value={formData.parentPhone}
                  onChange={handleInputChange('parentPhone')}
                  error={!!fieldErrors.parentPhone}
                  helperText={fieldErrors.parentPhone}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Phone sx={{ color: 'text.secondary' }} />
                      </InputAdornment>
                    ),
                  }}
                  disabled={isLoading}
                  placeholder="10-digit number"
                  required
                />
              </Box>

              <RegisterButton
                type="submit"
                fullWidth
                disabled={isLoading}
                startIcon={isLoading ? <CircularProgress size={20} /> : <PersonAdd />}
              >
                {isLoading ? 'Creating Account...' : 'Create Account'}
              </RegisterButton>
            </Box>

            <Divider sx={{ my: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Already have an account?
              </Typography>
            </Divider>

            <Box textAlign="center">
              <Button
                component={Link}
                to="/login"
                variant="outlined"
                fullWidth
                startIcon={<School />}
                sx={{
                  borderColor: '#667eea',
                  color: '#667eea',
                  borderRadius: 3,
                  py: 1.5,
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: '#764ba2',
                    background: 'rgba(102, 126, 234, 0.1)',
                  },
                }}
                disabled={isLoading}
              >
                Sign In Instead
              </Button>
            </Box>
          </CardContent>
        </RegisterCard>
    </RegisterContainer>
  );
};

export default RegisterPage; 